// Bus times, as a person reads them. Pure — no fetch, no DOM — so it runs under
// node (transit.test.mjs) and stays inside the copy-out folder's rules.
//
// EVERYTHING HERE IS FORMATTED IN WINNIPEG TIME, deliberately, and not in the
// viewer's. Two reasons, and they point the same way: almost every reader is
// standing at the stop, and the one who is not is reading a Winnipeg timetable
// — "the 6:04" is 6:04 in Winnipeg whoever is looking. Formatting in the
// device's zone would quietly retitle every departure for anybody travelling,
// which is exactly the person most likely to be checking a bus.
//
// The arithmetic is a different matter and uses the real instant `at` that the
// server attaches, so "in 4 min" is correct anywhere. The wall clock the
// timetable prints carries no offset at all (see api/_transit.js), which is why
// the two are kept apart rather than one being derived from the other.

const ZONE = 'America/Winnipeg';

const fmt = (opts) => new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, ...opts });

const asDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "6:04 am" — the time the timetable prints, in Winnipeg. */
export function clock(at) {
  const d = asDate(at);
  if (!d) return '';
  return fmt({ hour: 'numeric', minute: '2-digit', hour12: true })
    .format(d).replace(/\s?([ap])\.?m\.?/i, (_, p) => ` ${p.toLowerCase()}m`);
}

/** "9:23" — the same Winnipeg clock time with the meridiem dropped, for a pin. */
export function clockShort(at) {
  const d = asDate(at);
  if (!d) return '';
  const p = Object.fromEntries(
    fmt({ hour: 'numeric', minute: '2-digit', hour12: true })
      .formatToParts(d).map(x => [x.type, x.value])
  );
  return p.hour && p.minute ? `${p.hour}:${p.minute}` : '';
}

/** The Winnipeg calendar day of an instant, as "2026-08-30". */
export function dayKey(at) {
  const d = asDate(at);
  if (!d) return '';
  const p = Object.fromEntries(
    fmt({ year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(d).map(x => [x.type, x.value])
  );
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * "Today" / "Tomorrow" / "Mon, Sep 1".
 *
 * The list runs across days on purpose — a stop with one bus a day would
 * otherwise show ten identical-looking times — so every group needs a heading
 * that says which day it is, and the two nearest days deserve words rather than
 * a date somebody has to work out.
 */
export function dayLabel(at, now = Date.now()) {
  const key = dayKey(at);
  if (!key) return '';
  const today = dayKey(now);
  if (key === today) return 'Today';
  if (key === dayKey(new Date(asDate(now).getTime() + 86400e3))) return 'Tomorrow';
  return fmt({ weekday: 'short', month: 'short', day: 'numeric' }).format(asDate(at));
}

/**
 * Minutes until a departure, as somebody waiting would say it.
 *
 * "Due" rather than "0 min", because a bus at the stop is not a bus in zero
 * minutes. Past 90 minutes it becomes a time of day: "in 413 min" is a number
 * nobody converts, and the whole reason this list runs long is that the next
 * bus is sometimes hours away.
 */
export function inMinutes(at, now = Date.now()) {
  const d = asDate(at);
  if (!d) return '';
  const mins = Math.round((d.getTime() - asDate(now).getTime()) / 60000);
  if (mins < -1) return 'gone';
  if (mins <= 0) return 'due';
  if (mins === 1) return '1 min';
  if (mins <= 90) return `${mins} min`;
  return clock(d);
}

/**
 * How late a bus is running, from the scheduled/estimated pair.
 *
 * Null when there is nothing to compare — which is NOT "on time". Most
 * departures more than an hour out carry an estimate equal to the schedule
 * simply because no bus has been assigned yet, and calling that punctual would
 * be inventing a prediction the service never made.
 */
export function lateness(scheduled, estimated) {
  if (!scheduled || !estimated || scheduled === estimated) return null;
  const s = Date.parse(scheduled), e = Date.parse(estimated);
  if (Number.isNaN(s) || Number.isNaN(e)) return null;
  const mins = Math.round((e - s) / 60000);
  if (mins === 0) return null;
  return mins > 0 ? `${mins} min late` : `${-mins} min early`;
}

/**
 * The same wait, as a pin prints it: "due", "19m (9:23)", "6:04 am".
 *
 * Not a shorter `inMinutes` for its own sake — it is a different reading. The
 * panel is a list somebody is studying, so it can afford "12 min"; a pin is
 * glanced at over the top of two others, and the unit spelled out is the part
 * that pushes labels into each other.
 *
 * BOTH READINGS, because they answer different questions and a pin is the one
 * place there is no second line to put the other on. "19m" is what decides
 * whether to run; "(9:23)" is what you hold against the clock on the wall or
 * the start of your lecture. The panel makes the same pairing down its two
 * columns.
 *
 * Past 90 minutes it is a clock time alone: the minutes are a number nobody
 * converts (the same handover the panel makes), and a wait that long is not
 * about running for anything — so it keeps its am/pm, which at that distance
 * is genuinely ambiguous.
 *
 * A bus that has just gone returns '' rather than 'gone'. A pin has room for two
 * departures and they are the two you can still catch — a stale one sitting
 * there is worse than a shorter label.
 */
export function shortWait(at, now = Date.now(), { withClock = true } = {}) {
  const d = asDate(at);
  if (!d) return '';
  const mins = Math.round((d.getTime() - asDate(now).getTime()) / 60000);
  if (mins < 0) return '';
  if (mins === 0) return 'due';
  if (mins <= 90) {
    const t = withClock ? clockShort(d) : '';
    return t ? `${mins}m (${t})` : `${mins}m`;
  }
  return clock(d);
}

/**
 * What a stop's direction means to somebody on campus: "Exiting UofM" /
 * "Entering UofM".
 *
 * A compass word is the wrong unit for this reader. Two stops face each other
 * across Dafoe Road with nearly the same name, and the only thing that tells
 * them apart is Westbound versus Eastbound — which requires knowing which way
 * the campus lies from the road you are standing on. Nobody catching a bus
 * thinks in bearings; they think "am I going in or coming out". So the compass
 * word stays (it is what the sign and the timetable say) and this is added
 * beside it.
 *
 * Only the east–west pair is translated. Northbound and southbound stops sit on
 * University Crescent and Chancellor Matheson, which run ALONGSIDE campus
 * rather than out of it, so neither direction means leaving — and inventing an
 * answer for them would be worse than the compass word alone.
 */
export function campusDirection(direction) {
  const d = String(direction || '').trim().toLowerCase();
  if (d === 'westbound') return 'Exiting UofM';
  if (d === 'eastbound') return 'Entering UofM';
  return '';
}

/**
 * The next few departures as a pin can print them: `[{ route, when }]`.
 *
 * THREE, and only what is still catchable. Two answers "do I run for this one
 * or wait for the next"; the third answers the question underneath it — "if I
 * miss both, am I stranded" — which on this campus is a real question, because
 * several routes here run every 30 minutes and one of them is the last of the
 * evening. The cost is a pin ~7px taller (`busPinIcon` grows its canvas upward
 * so the tip stays on the kerb), which is the cheap direction: the pins that
 * crowd each other are the pairs facing across a road, and those are separated
 * horizontally.
 *
 * Cancelled departures are dropped rather than marked. There is no room to say
 * "cancelled" on a pin, and a route number sitting next to a time it will not
 * keep is a worse answer than the one below it.
 *
 * `clock: false` drops the "(9:23)" half, halving the label's width. That is
 * what lets the times show pulled further back: the countdown is the part that
 * still means something at a glance from across campus, and the clock time is
 * the part that would be printing over the next stop along.
 */
export function pinDepartures(departures, { max = 3, now = Date.now(), clock: withClock = true } = {}) {
  const out = [];
  for (const d of departures || []) {
    if (!d || d.cancelled) continue;
    const when = shortWait(d.at || d.scheduled, now, { withClock });
    if (!when) continue;
    const route = String(d.route?.number || d.route?.key || '').trim();
    if (!route) continue;
    out.push({ route, when });
    if (out.length >= max) break;
  }
  return out;
}

/** True while a departure is close enough that leaving now matters. */
export const isImminent = (dep, now = Date.now()) => {
  const d = asDate(dep?.at);
  if (!d) return false;
  const mins = (d.getTime() - asDate(now).getTime()) / 60000;
  return mins >= -1 && mins <= 15;
};

/**
 * Departures grouped into the days they fall on: `[{ key, label, departures }]`.
 *
 * Grouping rather than a flat list because the gaps are the interesting part of
 * a campus timetable — a five-hour overnight hole, or a Sunday with nothing
 * until tomorrow morning — and a flat list of ten times hides which of them are
 * even today.
 */
export function groupByDay(departures, now = Date.now()) {
  const out = [];
  for (const dep of departures || []) {
    const key = dayKey(dep.at || dep.scheduled);
    let group = out.length && out[out.length - 1].key === key ? out[out.length - 1] : null;
    if (!group) {
      group = { key, label: dayLabel(dep.at || dep.scheduled, now), departures: [] };
      out.push(group);
    }
    group.departures.push(dep);
  }
  return out;
}

/**
 * The routes to show as badges on a stop.
 *
 * Prefers what the service says serves the stop, and falls back to whatever the
 * departures themselves name. The fallback matters: the routes call and the
 * schedule call are independent, so one can fail while the other answers, and a
 * panel that has ten departures from route 60 should not claim it knows of no
 * routes here.
 */
export function stopRoutes(routes, departures) {
  const seen = new Map();
  for (const r of routes || []) if (r?.key && !seen.has(r.key)) seen.set(r.key, r);
  for (const d of departures || []) {
    const r = d?.route;
    if (r?.key && !seen.has(r.key)) seen.set(r.key, r);
  }
  return [...seen.values()];
}

/**
 * transit_stops.json → place-shaped entries the Nearby panel can list.
 *
 * A bus stop is not authored like the other places: Winnipeg Transit owns the
 * name, the number and the position, and the authoring tool adds only
 * `navNodeId` — the node of the outdoor path network somebody standing at the
 * stop walks from. Turning it into a place here means the pins, the
 * nearest-first list, the detail sheet and the deep link all work with no new
 * machinery.
 *
 * Two fields differ from every other place, on purpose:
 *
 *  · `lat`/`lng` are the POLE, so the pin lands on the kerb where the sign
 *    actually is. Everywhere else a place's position and its routing point are
 *    the same thing; here they cannot be, because the pole is not on our path
 *    network at all.
 *  · `routeNodeId` — not `navNodeId` — carries the routing point, precisely so
 *    that resolvePlace() keeps using the pole for the pin. Naming them apart is
 *    the whole reason a bus stop can be drawn in the right place and still send
 *    you somewhere you can walk.
 *
 * A stop with no node is still listed. It cannot offer Directions, but it can
 * still show you where it is and when the next bus goes, and those are most of
 * what anybody opens a bus stop for.
 */
export function transitStopPlaces(doc) {
  const out = [];
  for (const s of (doc?.stops || [])) {
    if (!Number.isFinite(s?.lat) || !Number.isFinite(s?.lng)) continue;
    const key = String(s.key);
    out.push({
      id: `busstop:${key}`,
      type: 'busstop',
      name: s.name || `Stop ${s.number ?? key}`,
      building: 'outdoors',
      floor: 0,
      lat: s.lat,
      lng: s.lng,
      routeNodeId: s.navNodeId || null,
      // The subtitle. The stop's name already carries the street on campus, so
      // this adds what it does not: the direction and the cross street, which
      // is what tells the two sides of one road apart.
      notes: stopWhere(s),
      transit: {
        key,
        number: s.number ?? null,
        routes: s.routes || [],
        direction: s.direction || null,
        street: s.street || null,
        crossStreet: s.crossStreet || null,
      },
    });
  }
  return out;
}

/** The Transit stop key behind a place, or '' for anything that is not a stop. */
export const stopKeyOf = (place) => (place?.type === 'busstop' ? String(place.transit?.key || '') : '');

/**
 * One line saying where a stop is, from the parts Transit gives.
 *
 * The name already carries the street on campus ("Westbound Dafoe at U of M
 * Station"), so this only adds what the name does not: the cross street, which
 * is what tells two stops on one block apart.
 */
export function stopWhere(stop) {
  if (!stop) return '';
  const bits = [];
  if (stop.direction) bits.push(stop.direction);
  if (stop.street && stop.crossStreet) bits.push(`${stop.street} at ${stop.crossStreet}`);
  else if (stop.street) bits.push(stop.street);
  return bits.join(' · ');
}
